import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { Redirect } from "react-router-dom";
import Spinner from '../../common/components/spinner/Spinner';
import * as actions from "../authentication/AuthenticationAction";
import { trimUserId } from './AuthenticationHandler';
import * as scorecardActions from '../../modules/scorecard/scorecardAction';
import { isEmpty } from 'lodash';
import * as betaStationActions from "../../modules/beta/betaStationAction";

const AuthenticationComponent = () => {
  // Local state using useState hook
  const [redirect, setRedirect] = useState(null);

  // Redux state using useSelector hook
  const {
    decodedToken,
    accessToken,
    gsrtStation
  } = useSelector(state => ({
    decodedToken: state.AuthenticationReducer.decodedToken,
    accessToken: state.AuthenticationReducer.accessToken,
    gsrtStation: state.AuthenticationReducer.registerData?.gsrtStation,
  }));

  const loginAsData = useSelector(state => state.loginReducer.loginAsData);

  // Dispatch and action binding
  const dispatch = useDispatch();
  const action = useMemo(() => 
    bindActionCreators({ 
      ...actions, 
      ...scorecardActions, 
      ...betaStationActions 
    }, dispatch), [dispatch]
  );

  // Memoized redirect logic (replaces getDerivedStateFromProps)
  useEffect(() => {
    if (accessToken) {
      const newRedirect = (gsrtStation || decodedToken?.amrcompany === 'AE') 
        ? "/task" 
        : "/scorecard";
      setRedirect(newRedirect);
    }
  }, [accessToken, gsrtStation, decodedToken?.amrcompany]);

  // Memoized storage event listener setup
  const setupStorageListener = useCallback((tokenData) => {
    const handleStorageChange = (e) => {
      const validToken = localStorage.getItem("token");
      if (validToken === null) {
        action.listenLogout(
          trimUserId(tokenData.SM_USER), 
          tokenData.SM_STATION, 
          tokenData.amrcompany
        );
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    // Return cleanup function
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [action]);

  // Main authentication effect (replaces componentDidMount)
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const code = urlParams.get("code");
    const tokenData = localStorage.getItem("tokenData");
    const token = localStorage.getItem("token");
    const apigeeToken = localStorage.getItem("apigeeToken");

    // No code and no token - listen for login
    if (!code && !token) {
      action.listenLogin();
      return;
    }

    // Code exists but no token - authenticate
    if (code && !token) {
      action.authenticateAction(code, tokenData, token);
      return;
    }

    // Token exists - setup authentication
    if (token) {
      const parsedTokenData = JSON.parse(tokenData || '{}');
      
      // Setup storage listener with cleanup
      const cleanupStorageListener = setupStorageListener(parsedTokenData);

      // Set auth token and authenticate
      action.setAuthToken(
        token,
        apigeeToken,
        parsedTokenData.SM_USER, 
        parsedTokenData.SM_STATION, 
        parsedTokenData.amrcompany
      );
      action.authenticateSuccess(parsedTokenData);

      // Handle user score for specific companies
      if (parsedTokenData.amrcompany === 'AA' || parsedTokenData.amrcompany === '') {
        const scoreParams = !isEmpty(loginAsData) 
          ? {
              employeeId: loginAsData.userId,
              stationCode: loginAsData.stationCode
            }
          : {
              employeeId: parsedTokenData.SM_USER,
              stationCode: parsedTokenData.SM_STATION
            };
        
        action.getUserScore(scoreParams);
      }

      // Initial data download
      action.initialDataDownload(
        trimUserId(parsedTokenData.SM_USER),
        "D_224097",
        parsedTokenData.SM_STATION,
        " ",
        " ",
        "Desktop",
        " ",
        parsedTokenData.amrcompany,
        " ",
        " ",
        " "
      );

      setRedirect("/task");

      // Cleanup function
      return cleanupStorageListener;
    }
  }, [action, loginAsData, setupStorageListener]);

  // Render logic
  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return <Spinner />;
};

export default AuthenticationComponent;